import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { companyService } from '../services/companyService';
import { fileService } from '../services/fileService';
import { CreateCompanyRequest, UpdateCompanyRequest, Company } from '../types/company';
import { useTranslation } from '../hooks/useTranslation';

interface CompanyFormProps {
  company?: Company;
  mode: 'create' | 'edit';
}

const CompanyForm: React.FC<CompanyFormProps> = ({ company, mode }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isDeletingLogo, setIsDeletingLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);

  // Инициализируем превью логотипа при загрузке компонента с существующей компанией
  useEffect(() => {
    if (company?.logoPath && company.logoPath.trim() !== '') {
      const logoUrl = fileService.getCompanyLogoUrl(company.logoPath);
      console.log('CompanyForm - Initializing logo preview:', { 
        logoPath: company.logoPath, 
        logoUrl,
        companyId: company.id,
        companyName: company.name
      });
      setLogoPreview(logoUrl);
    } else {
      // Если у компании нет логотипа, очищаем превью
      console.log('CompanyForm - No logo path, clearing preview');
      setLogoPreview(null);
    }
  }, [company?.logoPath, company?.id]);
  
  const { register, handleSubmit, formState: { errors } } = useForm<CreateCompanyRequest>({
    defaultValues: company || {
      name: '',
      taxId: '',
      vatId: '',
      country: '',
      city: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      logoPath: ''
    }
  });

  const createMutation = useMutation({
    mutationFn: companyService.createCompany,
    onSuccess: async (createdCompany) => {
      // Если есть логотип для загрузки, загружаем его
      if (logoFile) {
        try {
          setIsUploadingLogo(true);
          const response = await fileService.uploadCompanyLogo(createdCompany.id, logoFile);
          if (response.success && response.fileName) {
            // Обновляем превью с новым путем к логотипу
            setLogoPreview(fileService.getCompanyLogoUrl(response.fileName));
            setLogoFile(null);
          }
        } catch (error) {
          console.error('Error uploading logo after company creation:', error);
          setLogoError(t('validation.logoUploadError'));
        } finally {
          setIsUploadingLogo(false);
        }
      }
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      navigate('/companies');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCompanyRequest }) => 
      companyService.updateCompany(id, data),
    onSuccess: async (updatedCompany) => {
      // Если есть логотип для загрузки, загружаем его
      if (logoFile) {
        try {
          await handleLogoUpload(updatedCompany.id);
        } catch (error) {
          console.error('Error uploading logo after company update:', error);
        }
      }
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      navigate('/companies');
    }
  });

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Валидация файла
      const validation = fileService.validateFile(file);
      if (!validation.valid) {
        setLogoError(validation.error || t('validation.fileValidationError'));
        return;
      }

      setLogoError(null);
      setLogoFile(file);

      // Создание превью из выбранного файла
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      // Если файл не выбран, возвращаемся к текущему логотипу компании
      if (company?.logoPath) {
        setLogoPreview(fileService.getCompanyLogoUrl(company.logoPath));
      }
      setLogoFile(null);
    }
  };

  const handleLogoUpload = async (companyId: string) => {
    if (!logoFile) return;

    setIsUploadingLogo(true);
    setLogoError(null);

    try {
      const response = await fileService.uploadCompanyLogo(companyId, logoFile);
      if (response.success) {
        console.log('Logo uploaded successfully:', response.fileName);
        // Обновляем кэш запросов для обновления данных компании
        queryClient.invalidateQueries({ queryKey: ['companies'] });
        queryClient.invalidateQueries({ queryKey: ['company', companyId] });
        
        // Обновляем превью с новым путем к логотипу
        if (response.fileName) {
          setLogoPreview(fileService.getCompanyLogoUrl(response.fileName));
        }
        
        // Очищаем выбранный файл, так как он уже загружен
        setLogoFile(null);
      } else {
        setLogoError(response.error || t('validation.logoUploadError'));
      }
    } catch (error: any) {
      setLogoError(error.response?.data?.error || t('validation.logoUploadError'));
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!company?.id || !company?.logoPath) return;

    if (!window.confirm(t('companies.deleteLogoConfirm'))) {
      return;
    }

    setIsDeletingLogo(true);
    setLogoError(null);

    try {
      const response = await fileService.deleteCompanyLogo(company.id);
      if (response.success) {
        console.log('Logo deleted successfully');
        
        // Очищаем превью и файл сразу
        setLogoPreview(null);
        setLogoFile(null);
        
        // Обновляем кэш запросов и перезагружаем данные компании
        await queryClient.invalidateQueries({ queryKey: ['companies'] });
        await queryClient.invalidateQueries({ queryKey: ['company', company.id] });
        
        // Принудительно обновляем данные компании в кэше
        queryClient.setQueryData(['company', company.id], (oldData: Company | undefined) => {
          if (oldData) {
            return { ...oldData, logoPath: '' };
          }
          return oldData;
        });
        
        // Перезагружаем данные компании из сервера
        await queryClient.refetchQueries({ queryKey: ['company', company.id] });
      } else {
        setLogoError(response.error || t('companies.deleteLogoError'));
      }
    } catch (error: any) {
      setLogoError(error.response?.data?.error || t('companies.deleteLogoError'));
    } finally {
      setIsDeletingLogo(false);
    }
  };

  const onSubmit = async (data: CreateCompanyRequest) => {
    if (mode === 'create') {
      createMutation.mutate(data);
    } else if (company) {
      updateMutation.mutate({ id: company.id, data });
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {mode === 'create' ? t('companies.createCompany') : t('common.edit') + ' ' + t('companies.title').toLowerCase()}
        </h2>
        <p className="text-gray-600 mt-2">
          {mode === 'create' 
            ? t('companies.createDescription') 
            : t('companies.editDescription')
          }
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Название компании */}
          <div className="md:col-span-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              {t('companies.companyName')} *
            </label>
            <input
              type="text"
              id="name"
              {...register('name', { required: t('validation.companyNameRequired') })}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t('companies.companyNamePlaceholder')}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          {/* ИНН/Registration Number */}
          <div>
            <label htmlFor="taxId" className="block text-sm font-medium text-gray-700 mb-2">
              {t('companies.taxId')} *
            </label>
            <input
              type="text"
              id="taxId"
              {...register('taxId', { 
                required: t('validation.taxIdRequired'),
                minLength: {
                  value: 3,
                  message: t('validation.taxIdMinLength')
                },
                maxLength: {
                  value: 20,
                  message: t('validation.taxIdMaxLength')
                }
              })}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.taxId ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t('companies.taxIdPlaceholder')}
            />
            {errors.taxId && (
              <p className="mt-1 text-sm text-red-600">{errors.taxId.message}</p>
            )}
          </div>

          {/* VAT ID */}
          <div>
            <label htmlFor="vatId" className="block text-sm font-medium text-gray-700 mb-2">
              {t('companies.vatId')}
            </label>
            <input
              type="text"
              id="vatId"
              {...register('vatId', {
                maxLength: {
                  value: 20,
                  message: t('validation.vatIdMaxLength')
                }
              })}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.vatId ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t('companies.vatIdPlaceholder')}
            />
            {errors.vatId && (
              <p className="mt-1 text-sm text-red-600">{errors.vatId.message}</p>
            )}
          </div>

          {/* Телефон */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              {t('companies.phone')}
            </label>
            <input
              type="tel"
              id="phone"
              {...register('phone')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('companies.phonePlaceholder')}
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              {t('companies.email')}
            </label>
            <input
              type="email"
              id="email"
              {...register('email', {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('validation.emailInvalid')
                }
              })}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t('companies.emailPlaceholder')}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          {/* Веб-сайт */}
          <div>
            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
              {t('companies.website')}
            </label>
            <input
              type="url"
              id="website"
              {...register('website')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('companies.websitePlaceholder')}
            />
          </div>

          <div>
            <label htmlFor="logoPath" className="block text-sm font-medium text-gray-700 mb-2">
              {t('companies.logoPath')}
            </label>
            
            {/* Превью логотипа */}
            {(logoPreview || company?.logoPath) && (
              <div className="mb-4">
                <div className="relative inline-block">
                  <img
                    src={logoPreview || (company?.logoPath ? fileService.getCompanyLogoUrl(company.logoPath) : '')}
                    alt="Logo preview"
                    className="w-32 h-32 object-contain border border-gray-300 rounded-lg bg-gray-50 p-2"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      const src = target.src;
                      console.error('Failed to load logo preview. URL:', src);
                      console.error('Logo path:', company?.logoPath);
                      console.error('Logo preview state:', logoPreview);
                      // Не скрываем изображение, чтобы видеть проблему
                    }}
                    onLoad={() => {
                      console.log('Logo preview loaded successfully');
                    }}
                  />
                  {company?.logoPath && !logoFile && (
                    <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs px-2 py-1 rounded-bl-lg">
                      {t('companies.currentLogo')}
                    </div>
                  )}
                </div>
                {/* Кнопка удаления логотипа */}
                {mode === 'edit' && company?.logoPath && !logoFile && (
                  <button
                    type="button"
                    onClick={handleLogoDelete}
                    disabled={isDeletingLogo}
                    className="mt-2 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {isDeletingLogo ? t('companies.deleting') : t('companies.deleteLogo')}
                  </button>
                )}
              </div>
            )}

            {/* Загрузка файла */}
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <input
                  type="file"
                  id="logoFile"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={handleLogoChange}
                  className="hidden"
                />
                <label
                  htmlFor="logoFile"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
                >
                  <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  {t('companies.chooseFile')}
                </label>
                {logoFile && (
                  <span className="ml-3 text-sm text-gray-500">
                    {logoFile.name}
                  </span>
                )}
                {!logoFile && (
                  <span className="ml-3 text-sm text-gray-400">
                    {t('companies.noFileChosen')}
                  </span>
                )}
              </div>
              
              {logoFile && mode === 'edit' && company && (
                <button
                  type="button"
                  onClick={() => handleLogoUpload(company.id)}
                  disabled={isUploadingLogo}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploadingLogo ? t('companies.uploading') : t('companies.upload')}
                </button>
              )}
            </div>

            {/* Ошибки */}
            {logoError && (
              <p className="mt-2 text-sm text-red-600">{logoError}</p>
            )}

            <p className="text-sm text-gray-500 mt-1">
              {t('companies.logoSupport')}
            </p>
          </div>
        </div>

        {/* Адрес */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">{t('companies.companyAddress')}</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Страна */}
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                {t('companies.country')}
              </label>
              <input
                type="text"
                id="country"
                {...register('country')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('companies.countryPlaceholder')}
              />
            </div>

            {/* Город */}
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                {t('companies.city')} *
              </label>
              <input
                type="text"
                id="city"
                {...register('city', { required: t('validation.cityRequired') })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.city ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('companies.cityPlaceholder')}
              />
              {errors.city && (
                <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
              )}
            </div>
          </div>

          {/* Адрес */}
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
              {t('companies.address')}
            </label>
            <textarea
              id="address"
              {...register('address')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('companies.addressPlaceholder')}
            />
          </div>
        </div>

        {/* Кнопки */}
        <div className="flex justify-end space-x-4 pt-6">
          <button
            type="button"
            onClick={() => navigate('/companies')}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {t('companies.cancel')}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? t('companies.saving') : mode === 'create' ? t('companies.create') : t('companies.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyForm;
